import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Container } from "react-bootstrap";
import HeaderTwo from "../../components/HeaderTwo";
import { BreadcrumbBox } from "../../components/common/Breadcrumb";
import FooterTwo from "../../components/FooterTwo";
import { WHATSAPP_URL } from "../../components/common/siteLinks";
import {
  FILE_LIMITS,
  NO_ENGLISH_TEST,
  OTHER_COUNTRY,
  submitApplication,
} from "./submitApplication";
import "./styles/apply.css";

const STEPS = ["Personal Details", "Education", "Study Plans", "English Test", "Documents"];

const COUNTRIES = [
  "Canada",
  "Australia",
  "United Kingdom",
  "USA",
  "Hungary",
  "Lithuania",
  "Poland",
  "New Zealand",
  OTHER_COUNTRY,
];

const EDUCATION_LEVELS = [
  "Matric / O-Levels",
  "Intermediate / A-Levels",
  "Diploma",
  "Bachelor's",
  "Master's",
  "Other",
];

const STUDY_LEVELS = [
  "Certificate",
  "Diploma / Advanced Diploma",
  "Bachelor's Degree",
  "Postgraduate Diploma / Certificate",
  "Master's Degree",
  "PhD / Doctorate",
  "Other",
];

const FIELDS_OF_STUDY = [
  "Business & Management",
  "Computer Science & IT",
  "Engineering & Technology",
  "Health & Medicine",
  "Nursing",
  "Natural & Applied Sciences",
  "Social Sciences & Humanities",
  "Law & Legal Studies",
  "Arts, Design & Media",
  "Education & Teaching",
  "Hospitality & Tourism",
  "Agriculture & Environment",
  "Trades & Vocational",
  "Other",
];

const ENGLISH_TESTS = [NO_ENGLISH_TEST, "IELTS", "PTE", "TOEFL", "Duolingo", "OET", "Other"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CURRENT_YEAR = new Date().getFullYear();
const TODAY = new Date().toISOString().slice(0, 10);

const emptyEducation = () => ({ level: "", marks: "", year: "" });

const initialForm = {
  fullName: "",
  whatsapp: "",
  email: "",
  address: "",
  education: [emptyEducation()],
  country: "",
  countryOther: "",
  studyLevel: "",
  fieldOfStudy: "",
  courses: "",
  englishTest: "",
  englishScore: "",
  englishTestDate: "",
  additionalInfo: "",
  website: "", // honeypot: real visitors never see or fill this
};

const formatSize = (bytes) =>
  bytes < 1024 * 1024 ? `${Math.ceil(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

// Error keys match the id of the input they belong to, so the first one can be focused.
function validateStep(step, form) {
  const errors = {};
  const required = (id, value, message) => {
    if (!String(value).trim()) errors[id] = message;
  };

  if (step === 0) {
    required("fullName", form.fullName, "Please enter your full name.");
    const digits = form.whatsapp.replace(/\D/g, "");
    if (!form.whatsapp.trim()) errors.whatsapp = "Please enter your WhatsApp number.";
    else if (digits.length < 10 || digits.length > 15)
      errors.whatsapp = "Enter a valid number with country code, e.g. +92 300 1234567.";
    if (!form.email.trim()) errors.email = "Please enter your email address.";
    else if (!EMAIL_RE.test(form.email.trim())) errors.email = "Please enter a valid email address.";
    required("address", form.address, "Please enter your address.");
  }

  if (step === 1) {
    form.education.forEach((row, i) => {
      required(`education-${i}-level`, row.level, "Select a qualification.");
      required(`education-${i}-marks`, row.marks, "Enter your marks or grade.");
      const year = Number(row.year);
      if (!row.year.trim()) errors[`education-${i}-year`] = "Enter the year.";
      else if (!Number.isInteger(year) || year < 1970 || year > CURRENT_YEAR + 5)
        errors[`education-${i}-year`] = "Enter a valid year.";
    });
  }

  if (step === 2) {
    required("country", form.country, "Select your preferred country.");
    if (form.country === OTHER_COUNTRY) required("countryOther", form.countryOther, "Enter the country.");
    required("studyLevel", form.studyLevel, "Select the level you want to study.");
    required("fieldOfStudy", form.fieldOfStudy, "Select your field of study.");
    required("courses", form.courses, "List at least one course you would like to study.");
  }

  if (step === 3) {
    required("englishTest", form.englishTest, "Select an option.");
    if (form.englishTest && form.englishTest !== NO_ENGLISH_TEST) {
      required("englishScore", form.englishScore, "Enter your score.");
      if (!form.englishTestDate) errors.englishTestDate = "Enter the test date.";
      else if (form.englishTestDate > TODAY) errors.englishTestDate = "The test date can't be in the future.";
    }
  }

  return errors;
}

const Field = ({ id, label, required, error, hint, full, children }) => (
  <div className={`apply-field${full ? " apply-field-full" : ""}${error ? " has-error" : ""}`}>
    <label htmlFor={id}>
      {label}
      {required && <span className="apply-req" aria-hidden="true"> *</span>}
    </label>
    {children}
    {error ? (
      <small className="apply-error" id={`${id}-error`}>{error}</small>
    ) : (
      hint && <small className="apply-hint">{hint}</small>
    )}
  </div>
);

function Apply() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [fileError, setFileError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | done | error
  const topRef = useRef(null);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step, status]);

  const update = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const updateEducation = (index, name, value) => {
    setForm((prev) => ({
      ...prev,
      education: prev.education.map((row, i) => (i === index ? { ...row, [name]: value } : row)),
    }));
    setErrors((prev) => ({ ...prev, [`education-${index}-${name}`]: undefined }));
  };

  const addEducation = () =>
    setForm((prev) => ({ ...prev, education: [...prev.education, emptyEducation()] }));

  const removeEducation = (index) => {
    setForm((prev) => ({ ...prev, education: prev.education.filter((_, i) => i !== index) }));
    setErrors({});
  };

  const bind = (name) => ({
    id: name,
    name,
    value: form[name],
    onChange: (e) => update(name, e.target.value),
    "aria-invalid": errors[name] ? true : undefined,
    "aria-describedby": errors[name] ? `${name}-error` : undefined,
  });

  const bindEducation = (index, name) => {
    const id = `education-${index}-${name}`;
    return {
      id,
      value: form.education[index][name],
      onChange: (e) => updateEducation(index, name, e.target.value),
      "aria-invalid": errors[id] ? true : undefined,
      "aria-describedby": errors[id] ? `${id}-error` : undefined,
    };
  };

  const addFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    setFileError("");

    const next = [...files];
    const problems = [];
    picked.forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      const total = next.reduce((sum, f) => sum + f.size, 0);
      if (!FILE_LIMITS.extensions.includes(ext)) problems.push(`${file.name}: file type not allowed.`);
      else if (file.size > FILE_LIMITS.maxFileSize) problems.push(`${file.name}: larger than ${formatSize(FILE_LIMITS.maxFileSize)}.`);
      else if (next.length >= FILE_LIMITS.maxFiles) problems.push(`${file.name}: you can upload up to ${FILE_LIMITS.maxFiles} files.`);
      else if (total + file.size > FILE_LIMITS.maxTotalSize) problems.push(`${file.name}: total size would exceed ${formatSize(FILE_LIMITS.maxTotalSize)}.`);
      else if (!next.some((f) => f.name === file.name && f.size === file.size)) next.push(file);
    });

    setFiles(next);
    if (problems.length) setFileError(problems.join(" "));
  };

  const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (status === "submitting") return;

    const stepErrors = validateStep(step, form);
    if (Object.keys(stepErrors).length) {
      setErrors(stepErrors);
      document.getElementById(Object.keys(stepErrors)[0])?.focus();
      return;
    }
    setErrors({});

    if (step < STEPS.length - 1) {
      setStep(step + 1);
      return;
    }

    setStatus("submitting");
    try {
      await submitApplication(form, files);
      setStatus("done");
    } catch (err) {
      console.error("Application submission failed:", err);
      setStatus("error");
    }
  };

  const hasEnglishTest = form.englishTest && form.englishTest !== NO_ENGLISH_TEST;
  const isLastStep = step === STEPS.length - 1;

  return (
    <div className="main-wrapper apply-page">
      <HeaderTwo />
      <BreadcrumbBox title="Apply Now" />

      <section className="apply-area">
        <Container>
          <div className="apply-box" ref={topRef}>
            {status === "done" ? (
              <div className="apply-success" role="status">
                <div className="apply-success-icon" aria-hidden="true">
                  <i className="las la-check"></i>
                </div>
                <h3>We've received your request</h3>
                <p>
                  Thank you, {form.fullName.trim().split(/\s+/)[0]}. One of our senior counselors will
                  contact you soon on WhatsApp or at {form.email.trim()}.
                </p>
                <Link className="apply-btn-primary" to={process.env.PUBLIC_URL + "/"}>
                  Back to Home
                </Link>
              </div>
            ) : (
              <>
                <div className="apply-title">
                  <h3>Student Application</h3>
                  <p>Tell us about yourself and a counselor will guide you through the next steps.</p>
                </div>

                <ol className="apply-steps" aria-label="Application progress">
                  {STEPS.map((label, i) => (
                    <li
                      key={label}
                      className={i === step ? "active" : i < step ? "complete" : ""}
                      aria-current={i === step ? "step" : undefined}
                    >
                      <span className="apply-step-num">{i < step ? <i className="las la-check"></i> : i + 1}</span>
                      <span className="apply-step-label">{label}</span>
                    </li>
                  ))}
                </ol>

                <form className="apply-form" onSubmit={handleSubmit} noValidate>
                  <h4 className="apply-step-heading">
                    Step {step + 1} of {STEPS.length}: {STEPS[step]}
                  </h4>

                  {step === 0 && (
                    <div className="apply-grid">
                      <Field id="fullName" label="Full Name" required error={errors.fullName}>
                        <input type="text" autoComplete="name" placeholder="As on your passport" {...bind("fullName")} />
                      </Field>
                      <Field id="whatsapp" label="WhatsApp Number" required error={errors.whatsapp} hint="Include your country code.">
                        <input type="tel" autoComplete="tel" placeholder="+92 300 1234567" {...bind("whatsapp")} />
                      </Field>
                      <Field id="email" label="Email Address" required error={errors.email}>
                        <input type="email" autoComplete="email" placeholder="you@example.com" {...bind("email")} />
                      </Field>
                      <Field id="address" label="Address" required error={errors.address}>
                        <input type="text" autoComplete="street-address" placeholder="House, street, city, country" {...bind("address")} />
                      </Field>
                      <div className="apply-honeypot" aria-hidden="true">
                        <label htmlFor="website">Website</label>
                        <input type="text" tabIndex={-1} autoComplete="off" {...bind("website")} />
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <>
                      <p className="apply-intro">Add each qualification you've completed or are completing now.</p>
                      {form.education.map((row, i) => (
                        <fieldset className="apply-edu-row" key={i}>
                          <legend>Qualification {i + 1}</legend>
                          <div className="apply-grid apply-grid-3">
                            <Field id={`education-${i}-level`} label="Qualification" required error={errors[`education-${i}-level`]}>
                              <select {...bindEducation(i, "level")}>
                                <option value="">Select…</option>
                                {EDUCATION_LEVELS.map((level) => (
                                  <option key={level} value={level}>{level}</option>
                                ))}
                              </select>
                            </Field>
                            <Field id={`education-${i}-marks`} label="Marks / Grade" required error={errors[`education-${i}-marks`]}>
                              <input type="text" placeholder="e.g. 850/1100, 78%, 3.2 CGPA" {...bindEducation(i, "marks")} />
                            </Field>
                            <Field id={`education-${i}-year`} label="Completion Year" required error={errors[`education-${i}-year`]}>
                              <input type="number" inputMode="numeric" min="1970" max={CURRENT_YEAR + 5} placeholder={String(CURRENT_YEAR)} {...bindEducation(i, "year")} />
                            </Field>
                          </div>
                          {form.education.length > 1 && (
                            <button type="button" className="apply-link-btn" onClick={() => removeEducation(i)}>
                              <i className="las la-trash-alt" aria-hidden="true"></i> Remove
                            </button>
                          )}
                        </fieldset>
                      ))}
                      {form.education.length < 6 && (
                        <button type="button" className="apply-btn-outline" onClick={addEducation}>
                          <i className="las la-plus" aria-hidden="true"></i> Add another qualification
                        </button>
                      )}
                    </>
                  )}

                  {step === 2 && (
                    <div className="apply-grid">
                      <Field id="country" label="Preferred Country" required error={errors.country}>
                        <select {...bind("country")}>
                          <option value="">Select Country</option>
                          {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Field>
                      {form.country === OTHER_COUNTRY && (
                        <Field id="countryOther" label="Country name" required error={errors.countryOther}>
                          <input type="text" {...bind("countryOther")} />
                        </Field>
                      )}
                      <Field id="studyLevel" label="Study Level" required error={errors.studyLevel}>
                        <select {...bind("studyLevel")}>
                          <option value="">Select Study Level</option>
                          {STUDY_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                      </Field>
                      <Field id="fieldOfStudy" label="Field of Study" required error={errors.fieldOfStudy}>
                        <select {...bind("fieldOfStudy")}>
                          <option value="">Select Field of Study</option>
                          {FIELDS_OF_STUDY.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </Field>
                      <Field
                        id="courses"
                        label="Courses of Interest"
                        required
                        full
                        error={errors.courses}
                        hint="Please list up to three courses you would like to study, in order of preference."
                      >
                        <textarea rows="4" placeholder="1.&#10;2.&#10;3." {...bind("courses")} />
                      </Field>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="apply-grid">
                      <Field id="englishTest" label="English Test Taken" required error={errors.englishTest}>
                        <select {...bind("englishTest")}>
                          <option value="">Select…</option>
                          {ENGLISH_TESTS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </Field>
                      {hasEnglishTest && (
                        <>
                          <Field id="englishScore" label="Overall Score / Band" required error={errors.englishScore}>
                            <input type="text" placeholder="e.g. 6.5" {...bind("englishScore")} />
                          </Field>
                          <Field id="englishTestDate" label="Test Date" required error={errors.englishTestDate}>
                            <input type="date" max={TODAY} {...bind("englishTestDate")} />
                          </Field>
                        </>
                      )}
                    </div>
                  )}

                  {step === 4 && (
                    <>
                      <p className="apply-intro">
                        Upload your transcripts, certificates, passport and English test result if you have them.
                        PDF, JPG, PNG or Word files, up to {formatSize(FILE_LIMITS.maxFileSize)} each and{" "}
                        {formatSize(FILE_LIMITS.maxTotalSize)} in total.
                      </p>
                      <label className="apply-dropzone" htmlFor="documents">
                        <i className="las la-cloud-upload-alt" aria-hidden="true"></i>
                        <span>Choose files to upload</span>
                        <small>Optional: you can also send documents to your counselor later.</small>
                      </label>
                      <input
                        id="documents"
                        className="apply-file-input"
                        type="file"
                        multiple
                        accept={FILE_LIMITS.extensions.map((e) => `.${e}`).join(",")}
                        onChange={addFiles}
                        aria-describedby={fileError ? "documents-error" : undefined}
                      />
                      {fileError && <p className="apply-error" id="documents-error" role="alert">{fileError}</p>}
                      {files.length > 0 && (
                        <ul className="apply-file-list">
                          {files.map((file, i) => (
                            <li key={`${file.name}-${file.size}`}>
                              <i className="las la-file-alt" aria-hidden="true"></i>
                              <span className="apply-file-name">{file.name}</span>
                              <span className="apply-file-size">{formatSize(file.size)}</span>
                              <button type="button" onClick={() => removeFile(i)} aria-label={`Remove ${file.name}`}>
                                <i className="las la-times" aria-hidden="true"></i>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="apply-grid">
                        <Field
                          id="additionalInfo"
                          label="Additional Information or Comments"
                          full
                          hint="Optional. Anything else your counselor should know."
                        >
                          <textarea rows="4" {...bind("additionalInfo")} />
                        </Field>
                      </div>
                    </>
                  )}

                  {status === "error" && isLastStep && (
                    <p className="apply-submit-error" role="alert">
                      We couldn't send your application. Please check your internet connection and try again, or{" "}
                      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">message us on WhatsApp</a>.
                    </p>
                  )}

                  <div className="apply-actions">
                    {step > 0 && (
                      <button
                        type="button"
                        className="apply-btn-outline"
                        onClick={() => { setErrors({}); setStep(step - 1); }}
                        disabled={status === "submitting"}
                      >
                        Back
                      </button>
                    )}
                    <button type="submit" className="apply-btn-primary" disabled={status === "submitting"}>
                      {isLastStep ? (status === "submitting" ? "Submitting…" : "Submit Application") : "Next"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </Container>
      </section>

      <FooterTwo />
    </div>
  );
}

export default Apply;
